import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { FileUpload, Icon } from '@PetSureAustralia/petsure-react-ui-library/distLib/components';
import { ExamplePopupTrigger } from '../../../examplePopups/examplePopupTrigger';
import { UploadedFilePropTypes } from '../../../../propTypes/claimDetails';
import { useFileUpload } from './useFileUpload';
import { VALIDATION_ERROR_MESSAGE } from '../../../../constants/errorMessage';
import { FORM_KEYS } from '../../../../constants/formKeys';
import { TooltipTrigger } from '../../../tooltipTrigger/tooltipTrigger';

import { FeatureFlagContext } from '../../../../contextProviders/featureFlagContext';
import Loading from '../../../shared/Loading/Loading';
import { getGtmService } from '../../../../services/gtmService/GtmService';
import { DOCUMENT_TYPE } from '../../../../services/gtmService/consts';

export const UploadVetNotes = ({ isReadOnly, vetNotes, policyNumber }) => {
  const { cspConfiguration } = useContext(FeatureFlagContext);
  const isLecBrand = cspConfiguration?.IsLEC || false;

  const { files, loading, validationError, handleRemoveFile, handleFileChange, formContext } = useFileUpload({
    incomingFiles: vetNotes,
    formKey: FORM_KEYS.VET_NOTES,
    requiredErrorMessage: VALIDATION_ERROR_MESSAGE.REQUIRED_VET_NOTES
  });

  const vetConsultationFileValidation = !isLecBrand
    ? {
        required: validationError(),
        maxFileCountLimit: VALIDATION_ERROR_MESSAGE.MAX_FILE_COUNT,
        unsupportedFileType: VALIDATION_ERROR_MESSAGE.UNSUPPORTED_FILE_TYPE
      }
    : {};

  const handleFileChangeWithGtm = (event) => {
    handleFileChange(event);

    const gtm = getGtmService();
    gtm.pushDocumentUpload(DOCUMENT_TYPE.VET_NOTES, policyNumber);
  };

  return (
    <fieldset>
      {isLecBrand ? (
        <>
          <h3>
            To speed up your claim, upload any vet consultation notes you have (optional) {` `}
            <TooltipTrigger
              id="tip-click"
              mode="click"
              trigger={<Icon icon="information" />}
              content={<>These are the veterinarian`s medical notes from examination findings.</>}
            />
          </h3>
          <p>Vet consultation notes for the life of your pet are generally required for your first claim.</p>
          <p>
            You can submit your claim without vet consultation notes, but in some cases we are not able to process the
            claim without them. If this is the case, we will contact your vet and take care of this for you.
          </p>
        </>
      ) : (
        <>
          <h3>Upload all vet consultation notes you have related to this claim.</h3>
          <p>These are the medical notes from examination findings and are required to process your claim.</p>
          <ExamplePopupTrigger exampleKey={FORM_KEYS.VET_NOTES} />
          <p>If this is your first claim, vet consultation notes for the life of your pet are generally required.</p>
        </>
      )}

      {loading ? (
        <Loading />
      ) : (
        <FileUpload
          name={FORM_KEYS.VET_NOTES}
          inputType="file"
          label="Select file/s"
          labelStyle="button"
          listStyle="reveal"
          fieldFormContext={formContext}
          customCssClass={formContext.formState.errors.vetNotes ? 'fileUploadCustomError' : ''}
          validation={vetConsultationFileValidation}
          readOnly={isReadOnly}
          placeholder="Vet consultation notes"
          validFiles={{
            extensions: [
              'application/pdf',
              'image/apng',
              'image/gif',
              'image/jpeg',
              'image/pjpeg',
              'image/png',
              'image/tiff',
              'image/webp'
            ],
            fileTypes: 'documents',
            supportsLabel: 'pdfs, png, apng, jpeg, pjpeg, png, tiff, webp',
            maxFileSize: 10485760, // 10 MB
            maxFileCount: 8
          }}
          defaultSelectedFiles={files}
          customRemoveHandler={handleRemoveFile}
          customChangeHandler={handleFileChangeWithGtm}
        />
      )}
    </fieldset>
  );
};

UploadVetNotes.propTypes = {
  isReadOnly: PropTypes.bool,
  vetNotes: PropTypes.arrayOf(PropTypes.shape(UploadedFilePropTypes)),
  policyNumber: PropTypes.string
};
